import { Overlay, RepositionScrollStrategy } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Provider,
} from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  MAT_SELECT_SCROLL_STRATEGY,
  MatSelectModule,
} from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { NgApiDocumentationModule } from '@pishgamrayan/ng-api-documentation';
import { NgFormKitModule } from '@pishgamrayan/ng-form-kit';
import { LibNgOnlineSurveyModule } from '@pishgamrayan/ng-online-survey';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { routes } from './app-routing.module';
import { StorageKeys } from './configs/storage-keys-config';
import { ApiConfiguration } from './core/api/api-configuration';
import { appGraphqlProvider } from './core/graphql/graphql.provider';
import { AuthGuard } from './core/guards/auth.guard';
import { ClientDataGuard } from './core/guards/client-data.guard';
import { HttpActiveOrganizationInterceptor } from './core/interceptors/active-organization.Interceptor';
import { HttpCheckRequestInterceptorProviders } from './core/interceptors/check-request.Interceptor';
import { HttpCultureInterceptorProviders } from './core/interceptors/culture.Interceptor';
import { HttpErrorInterceptorProviders } from './core/interceptors/error.Interceptor';
import { HttpLoadingInterceptorProviders } from './core/interceptors/loading.Interceptor';
import { HttpLoginInterceptorProviders } from './core/interceptors/login.Interceptor';
import { HttpPermissionInterceptorProviders } from './core/interceptors/permission.Interceptor';
import { HttpPreventDuplicateRequestInterceptorProviders } from './core/interceptors/prevent-duplicate-requests.Interceptor';
import { HttpServerDateTimeInterceptorProviders } from './core/interceptors/server-date-time.Interceptor';
import { TimeoutInterceptorProviders } from './core/interceptors/timeout.Interceptor';
import { HttpTokenInterceptorProviders } from './core/interceptors/token.Interceptor';
import { ClientDataInitializerService } from './core/services/authentication/client-data-initializer.service';
import { GetApiUrlInitializerService } from './core/services/authentication/get-api-url-initializer.service';
import { AppConfigService } from './core/services/config.service';
import { ConsoleService } from './core/services/console.service';

export function scrollFactory(
  overlay: Overlay,
): () => RepositionScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

export const INIT_APP_CONFIGURATION: Provider = {
  provide: APP_INITIALIZER,
  useFactory:
    (
      getApiDataService: GetApiUrlInitializerService,
      apiConfigService: ApiConfiguration,
      getCurrentClientDataService: ClientDataInitializerService,
    ) =>
    () =>
      getApiDataService.init().then(() => {
        let apiUrl = '';

        const localApiUrl: string = localStorage.getItem(StorageKeys.apiUrl);

        const config = AppConfigService.config;

        if (!config.production && localApiUrl) apiUrl = localApiUrl;
        else apiUrl = config.apiUrl;

        if (apiUrl) {
          apiConfigService.rootUrl = apiUrl;

          return getCurrentClientDataService.init();
        } else {
          return new Promise<boolean>(() => {
            ConsoleService.info('no connection to api');
          });
        }
      }),
  deps: [
    GetApiUrlInitializerService,
    ApiConfiguration,
    ClientDataInitializerService,
    AppConfigService,
  ],
  multi: true,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideToastr({
      timeOut: 5000,
      progressBar: true,
      positionClass: 'toast-top-center',
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning',
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      MatSelectModule,
      NgxMatSelectSearchModule,
      MatDialogModule,
      MatSnackBarModule,
      MatAutocompleteModule,
      TranslateModule.forRoot(),
      InlineSVGModule.forRoot(),
      MonacoEditorModule.forRoot({}),
      NgApiDocumentationModule.forRoot({
        apiUrl: 'https://smspanel.pishgamrayan.com',
        projectName: 'smsPanel',
        homePageUrl: '/',
        pageRoute: 'documentation',
        logoPath: '/assets/media/logos/logo-with-bg.svg',
        themeStorageKey: StorageKeys.themeMode,
      }),
      LibNgOnlineSurveyModule.forRoot({
        apiUrl: 'https://smspanel.pishgamrayan.com/OnlineSurvey',
      }),
      NgFormKitModule.forRoot({
        hasDefaultZero: true,
      }),
    ),
    INIT_APP_CONFIGURATION,
    ClientDataGuard,
    AuthGuard,
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js'),
    }),

    HttpCheckRequestInterceptorProviders,
    HttpServerDateTimeInterceptorProviders,
    HttpTokenInterceptorProviders,
    HttpLoginInterceptorProviders,
    HttpCultureInterceptorProviders,
    HttpActiveOrganizationInterceptor,
    HttpLoadingInterceptorProviders,
    HttpPermissionInterceptorProviders,
    HttpErrorInterceptorProviders,
    ...TimeoutInterceptorProviders,
    HttpPreventDuplicateRequestInterceptorProviders,
    appGraphqlProvider,
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerImmediately',
    }),
    {
      provide: 'FORM_KIT_CONFIG',
      useValue: {
        hasDefaultZero: true,
      },
    },
  ],
};
